// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import {
  UpdateInvoiceInput,
  updateInvoice,
} from '@/billing/api/update-invoice';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// User-facing codes the caller maps to field-level helper text. Skip
// Sentry — these are expected validation errors, not unexpected failures.
const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_number_duplicate,
  ERROR_CODE_MAPPING.invoice_customer_not_found,
]);

interface UseUpdateInvoiceOptions {
  invoiceKey: string;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function useUpdateInvoice(options: UseUpdateInvoiceOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateInvoiceInput) => updateInvoice(input),
    onSuccess: async () => {
      await invalidateInvoiceQueries({
        queryClient,
        invoiceKey: options.invoiceKey,
      });
      options.onSuccess?.();
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      if (
        !apiError.sentToSentry &&
        !EXPECTED_ERROR_CODES.has(apiError.apiStatusCode)
      ) {
        Sentry.captureException(error);
      }
      options.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
